import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CartItem, ShippingMethod } from '../types/shipping';

// Forma mínima de producte que necessita aquest hook per treure'n el preu.
// Els components tenen interfícies de Product més riques, però són
// estructuralment compatibles amb aquesta.
interface ShippingProduct {
  id: number;
  price: string;
}

interface ShippingLineItem {
  product_id: number;
  quantity: number;
  price: number;
}

interface UseShippingQuoteParams {
  countryCode: string;
  state: string;
  postcode: string;
  city: string;
  items: CartItem[];
  products: ShippingProduct[];
  enabled?: boolean;
}

// Petit helper de debounce, només per als camps de text lliure (codi postal
// i ciutat): evita disparar una consulta a cada tecla premuda. País, estat i
// el carret no es debouncen: han de disparar la consulta immediatament.
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timeoutId);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Font de veritat única per consultar els mètodes d'enviament a WooCommerce.
 * Substitueix les tres implementacions independents (Cart, CartIcon,
 * Products): usa useQuery (no useMutation) perquè React Query pugui avortar
 * automàticament (via `signal`) les peticions superades per una de més
 * recent, eliminant les curses. Sense placeholderData/keepPreviousData:
 * mentre es recalcula, `methods` és buit en lloc de mostrar un cost obsolet.
 */
export function useShippingQuote({
  countryCode,
  state,
  postcode,
  city,
  items,
  products,
  enabled
}: UseShippingQuoteParams) {
  const debouncedPostcode = useDebouncedValue(postcode, 400);
  const debouncedCity = useDebouncedValue(city, 400);

  // Line items amb el preu actual del producte, ordenats per product_id
  // perquè la queryKey sigui estable entre renders encara que l'ordre dels
  // items al carret canviï.
  //
  // IMPORTANT: agrupem per product_id. WooCommerce fa add_to_cart() per cada
  // línia rebuda i, si el mateix product_id hi arriba dues vegades, SUMA les
  // quantitats (verificat contra l'endpoint: 6+6 del mateix producte factura
  // com a 12). Enviant una sola línia per producte, un carret duplicat mai
  // pot inflar el cost d'enviament.
  const lineItems = useMemo<ShippingLineItem[]>(() => {
    const byProductId = new Map<number, ShippingLineItem>();

    items
      .filter(item => item.quantity > 0)
      .forEach(item => {
        const existing = byProductId.get(item.productId);
        if (existing) {
          existing.quantity += item.quantity;
          return;
        }

        const product = products.find(p => p.id === item.productId);
        const price = product ? parseFloat(product.price.replace(',', '.')) : 0;
        byProductId.set(item.productId, {
          product_id: item.productId,
          quantity: item.quantity,
          price: isNaN(price) ? 0 : price
        });
      });

    return [...byProductId.values()].sort((a, b) => a.product_id - b.product_id);
  }, [items, products]);

  const lineItemsKey = JSON.stringify(lineItems);

  // Mentre l'usuari no hagi introduït un codi postal/ciutat reals (mini-carret
  // i llistat de productes no tenen aquests camps), fem servir uns valors
  // provisionals. Els resolem AQUÍ i no dins la queryFn perquè formin part de
  // la queryKey: si no, un canvi de codi postal provisional no invalidaria la
  // consulta i es continuaria mostrant una estimació calculada amb l'anterior.
  const effectivePostcode = debouncedPostcode || localStorage.getItem('tempPostcode') || '0000';
  const effectiveCity = debouncedCity || localStorage.getItem('tempCity') || 'Capital';

  const query = useQuery<ShippingMethod[]>({
    queryKey: ['shipping', countryCode, state, effectivePostcode, effectiveCity, lineItemsKey],
    queryFn: async ({ signal }) => {
      const currentPostcode = effectivePostcode;
      const currentCity = effectiveCity;

      const response = await fetch(`/api/shipping/${countryCode}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          postcode: currentPostcode,
          city: currentCity,
          state,
          items: lineItems
        }),
        signal
      });

      if (!response.ok) {
        throw new Error('Error calculant l\'enviament');
      }

      return response.json();
    },
    enabled: enabled !== false && !!countryCode && !!state && lineItems.length > 0 && products.length > 0,
    staleTime: 0,
    gcTime: 0,
    retry: 0,
    refetchOnWindowFocus: false
  });

  return {
    methods: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch
  };
}
